import React from "react";
import styled from "styled-components/macro";
import { Link } from "react-router-dom";
import { useAuth } from "@src/contexts/AuthProvider";
import { Button } from "@src/components/elements";
import { PeopleIcon, ProfileIcon } from "@src/components/elements/assets";
import { useSession } from "@src/contexts/SessionContext";
import { Page } from "@src/shared/pages";
import { useSwitchToCompany } from "@src/pages/SuperAdmin/useSwitchToCompany";
import {
  ActionMenu,
  StyledActionLink,
} from "@src/components/elements/ActionMenu";
import { NotificationIcon } from "@src/components/elements/assets/NotificationIcon";
import { LogoutIcon } from "@src/components/elements/assets/LogoutIcon";
import { SuperAdminIcon } from "@src/components/elements/assets/SuperAdminIcon";
import { Chip } from "@src/components/elements/Chip";
import { ArrowIcon } from "@src/components/elements/assets/ArrowIcon";
import { useModal } from "@src/hooks";
import { ImpersonationModal } from "@src/components/modules/Header/ImpersonationModal";
import { PermissionChips } from "@src/components/modules/PermissionChips/PermissionChips";
import { isSuperAdmin } from "@src/sudozi-schemas/is-super-admin";
import { UserName } from "@src/components/modules/UserName";
import { useRememberMe } from "@src/pages/useRememberMe";

const StyledRoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--inter-font);
  padding: 0.75rem 1.5rem;
  transition: var(--transition);
  border-bottom: 1px solid var(--dark-gray);
  background-color: var(--secondary-black);
  box-shadow: var(--drop-shadow-dark);
  color: var(--light-gray);
  position: sticky;
  top: 0;

  &:hover {
    background: var(--dark-gray);
    color: var(--white);
    a {
      text-decoration: none;
    }
  }

  .user-name-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-start;
    cursor: pointer;
    width: 100%;
  }

  .user-name {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-small);
    color: var(--white);
  }

  .current-company-name {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-regular);
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .mobile-only {
    display: none;
  }

  @media screen and (max-width: 40rem) {
    .mobile-only {
      display: block;
      fill: var(--secondary-black);
    }
  }
`;

const MenuStyleRoot = styled.div`
  .icon {
    zoom: 1.5;
    width: 1rem;
    height: 1rem;
    margin-right: 0.25rem;
  }

  .line {
    margin: 0.5rem;
  }

  .subhead {
    font-size: var(--font-size-xsmall);
    font-weight: var(--font-weight-bold);
    color: var(--gray);
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  .non-clickable {
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-gray-light);
  }
`;

export const CurrentUser: React.FC = () => {
  const { removeToken, tokenUserData } = useAuth();
  const { setRememberedEmail } = useRememberMe();

  const handleLogout = async () => {
    // redirecting to home page so that when the user is logs back in,
    // they get redirected to the home page and not the page they were on
    await fetch("/clear-jwt", { method: "DELETE" });
    setRememberedEmail("");
    removeToken();
    window.location.href = location.href.startsWith("/v")
      ? Page.VENDOR_PORTAL_LOGIN
      : Page.LOGIN;
  };

  const {
    currentCompany,
    companyMemberships,
    error,
    vendorUser,
    vendorCompany,
  } = useSession();

  const { switchToCompany } = useSwitchToCompany();
  const [impersonationModal, { showModal: showImpersonationModal }] = useModal({
    title: "View as other member",
    content: <ImpersonationModal />,
  });

  if (vendorUser && vendorCompany) {
    const actionButton = (
      <div className="row">
        <div className="user-name-container">
          <ProfileIcon className="mobile-only" />
          <div className="user-name">
            <UserName user={vendorUser} />
          </div>
          <div className="current-company-name">
            {error && error.message}
            {vendorCompany.name}
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 0 24 24"
          width="24px"
          fill="#000000"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    );

    return (
      <StyledRoot>
        <ActionMenu actionButton={actionButton} offset={{ x: 3, y: 3 }}>
          <MenuStyleRoot>
            <div className="non-clickable">
              <div title={`Company ID: ${currentCompany?.id}`} className="line">
                {vendorUser.email}
              </div>
            </div>

            <StyledActionLink
              to={Page.VENDOR_PORTAL_COMPANY_SELECTION}
              className="group"
            >
              Change company
            </StyledActionLink>

            <StyledActionLink
              to="#"
              onClick={handleLogout}
              className="group danger-font"
            >
              <LogoutIcon className="icon" />
              Log Out
            </StyledActionLink>
          </MenuStyleRoot>
        </ActionMenu>
      </StyledRoot>
    );
  }

  if (tokenUserData && currentCompany) {
    const actionButton = (
      <div className="row start">
        <ProfileIcon fill="var(--green)" />
        <div className="user-name-container">
          <div className="user-name">
            <UserName user={tokenUserData} />
          </div>
          <div className="current-company-name">
            {error && error.message}
            {currentCompany && currentCompany?.name}
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 0 24 24"
          width="24px"
          fill="#000000"
        >
          <path d="M0 0h24v24H0z" fill="none" />
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    );

    return (
      <>
        {impersonationModal}

        <StyledRoot>
          <ActionMenu
            className="full-width"
            actionButton={actionButton}
            offset={{ x: 3, y: 3 }}
          >
            <MenuStyleRoot className="full-width">
              <div className="non-clickable">
                <div
                  title={`Company ID: ${currentCompany?.id}`}
                  className="line"
                >
                  {tokenUserData.email}
                </div>

                <div className="line">
                  <PermissionChips permissions={tokenUserData} />
                </div>

                <div className="line">
                  <div className="subhead">Departments</div>
                  <div>
                    {tokenUserData.departmentNames?.map((name) => (
                      <Chip disabled={true} key={name}>
                        {name}
                      </Chip>
                    ))}
                  </div>
                </div>

                {tokenUserData.businessUnitNames &&
                  tokenUserData.businessUnitNames.length > 0 && (
                    <div className="line">
                      <div className="subhead">Business units</div>
                      <div>
                        {tokenUserData.businessUnitNames?.map((name) => (
                          <Chip disabled={true} key={name}>
                            {name}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              <StyledActionLink
                to={Page.USER_NOTIFICATION_SETTINGS}
                className="group success-font"
              >
                <NotificationIcon className="icon" /> Account settings
              </StyledActionLink>

              {companyMemberships?.map((companyMembership) => {
                if (currentCompany?.id === companyMembership.companyId) {
                  return null;
                }
                return (
                  <StyledActionLink
                    to=""
                    key={companyMembership.id}
                    className="group success-font"
                    onClick={() =>
                      switchToCompany(
                        companyMembership.companyId,
                        companyMembership.company.name,
                      )
                    }
                  >
                    <ArrowIcon className="icon" fill="black" /> Switch to{" "}
                    {companyMembership.company.name}
                  </StyledActionLink>
                );
              })}

              {tokenUserData.hasAdminAccess ? (
                <StyledActionLink
                  to=""
                  className="group"
                  onClick={showImpersonationModal}
                >
                  <PeopleIcon className="icon" />
                  View as other member
                </StyledActionLink>
              ) : null}

              {isSuperAdmin(tokenUserData.email) && (
                <StyledActionLink
                  to={Page.SUPER_ADMIN}
                  className="group success-font"
                >
                  <SuperAdminIcon className="icon" />
                  Super admin page
                </StyledActionLink>
              )}

              <StyledActionLink
                to="#"
                onClick={handleLogout}
                className="group danger-font"
              >
                <LogoutIcon className="icon" />
                Log Out
              </StyledActionLink>
            </MenuStyleRoot>
          </ActionMenu>
        </StyledRoot>
      </>
    );
  }
  return (
    <Button className="margin-sides-xlarge">
      <Link to={Page.LOGIN}>Log in</Link>
    </Button>
  );
};
